import axios, { AxiosRequestConfig } from 'axios';
import { ApiEnvelope, unwrapSavedEntity } from './apiEnvelope';
import { Entree, FaceAFaceAvecDto } from '../types/api';

/**
 * Configuration pour les requêtes API avec authentification
 * @param accessToken - Token d'accès Azure B2C
 * @returns Configuration axios avec headers d'authentification
 */
const createConfig = (accessToken: string, signal?: AbortSignal): AxiosRequestConfig => ({
    headers: {
        'Authorization': `Bearer ${accessToken}`,
        'AccessToken': accessToken,
        'Content-Type': 'application/json'
    },
    // Sans signal, une requête longue (le dictionnaire entier) ne rend la main
    // qu'à son terme : « Annuler » resterait sans effet jusque-là.
    ...(signal ? { signal } : {})
});

/**
 * Taille de page demandée par `getEntrees`. Exportée parce qu'un appelant a besoin
 * de savoir que sa liste peut être TRONQUÉE : au-delà, des entrées manquent sans
 * que rien ne le dise (voir l'export Markdown, rubrique des fiches non générées).
 */
export const ENTREES_PAGE_LIMIT = 1000;

/**
 * Service API pour la gestion des entrées
 * Toutes les méthodes sont typées avec TypeScript strict
 */
export const entreeService = {
    /**
     * Récupère toutes les entrées de l'utilisateur
     * @param accessToken - Token d'authentification
     * @param signal - Signal d'annulation (optionnel) : coupe la requête en vol
     * @returns Liste des entrées, dans la limite de `ENTREES_PAGE_LIMIT`
     */
    getEntrees: async (accessToken: string, signal?: AbortSignal): Promise<Entree[]> => {
        try {
            // Demander une limite élevée pour récupérer toutes les entrées.
            // L'API répond par l'enveloppe { success, data: Entree[], pagination } :
            // renvoyer l'enveloppe brute ferait planter tout appelant qui itère la liste.
            const response = await axios.get<{ success: boolean; data?: Entree[] }>(
                `/api/entrees?limit=${ENTREES_PAGE_LIMIT}`,
                createConfig(accessToken, signal)
            );
            const entrees = response.data?.data;
            if (!Array.isArray(entrees)) {
                throw new Error('Réponse inattendue de /api/entrees : liste absente');
            }
            return entrees;
        } catch (error) {
            console.error('Erreur lors de la récupération des entrées:', error);
            throw new Error('Impossible de récupérer les entrées');
        }
    },

    /**
     * Sauvegarde une entrée (création ou mise à jour)
     * @param entree - Entrée à sauvegarder
     * @param accessToken - Token d'authentification  
     * @returns Entrée sauvegardée avec ID
     */
    save: async (entree: Partial<Entree>, accessToken: string): Promise<Entree> => {
        try {
            if (!entree._id) {
                // Nouvelle entrée
                console.log('Entree save async - création');
                const response = await axios.post<ApiEnvelope<Entree> | Entree>('/api/entrees', entree, createConfig(accessToken));
                return unwrapSavedEntity<Entree>(response.data, "de l'entrée");
            } else {
                // Mise à jour entrée existante
                console.log('Entree update async - modification');
                const response = await axios.put<ApiEnvelope<Entree> | Entree>(`/api/entrees/${entree._id}`, entree, createConfig(accessToken));
                return unwrapSavedEntity<Entree>(response.data, "de l'entrée");
            }
        } catch (error) {
            console.error('Erreur lors de la sauvegarde de l\'entrée:', error);
            throw new Error('Impossible de sauvegarder l\'entrée');
        }
    },

    /**
     * Supprime une entrée
     * @param entree - Entrée à supprimer
     * @param accessToken - Token d'authentification
     * @returns Entrée supprimée pour confirmation
     */
    delete: async (entree: Entree, accessToken: string): Promise<Entree> => {
        try {
            console.log('Entree delete async');
            await axios.delete(`/api/entrees/${entree._id}`, createConfig(accessToken));
            return entree;
        } catch (error) {
            console.error('Erreur lors de la suppression de l\'entrée:', error);
            throw new Error('Impossible de supprimer l\'entrée');
        }
    },

    /**
     * Recherche d'entrées par clé ou valeur
     * @param query - Terme de recherche
     * @param accessToken - Token d'authentification
     * @returns Entrées correspondant à la recherche
     */
    search: async (query: string, accessToken: string): Promise<Entree[]> => {
        try {
            const response = await axios.get<Entree[]>(
                `/api/entrees/search?q=${encodeURIComponent(query)}`, 
                createConfig(accessToken)
            );
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la recherche d\'entrées:', error);
            throw new Error('Impossible d\'effectuer la recherche');
        }
    },

    /**
     * « Face à face avec… » (C23 SP6) : qui, parmi les notes que je peux lire, a marqué un concept
     * qui répond à l'un des miens. 404 quand le concept n'est pas à moi.
     */
    getFaceAFaceAvec: async (entreeId: string, accessToken: string): Promise<FaceAFaceAvecDto> => {
        const response = await axios.get(`/api/entrees/${encodeURIComponent(entreeId)}/face-a-face-avec`, createConfig(accessToken));
        return response.data.data;
    },
};

export default entreeService;
